
import { ArrowDownCircle, Package2, Clock, Calendar } from "lucide-react";
import StatsCard from "./StatsCard";
import { format } from "date-fns";
import { Product } from "@/lib/types";
import { useState, useEffect } from "react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { calculateProductUsageRate } from "@/lib/storage/analytics/remote-data";
import { useToast } from "@/hooks/use-toast";

interface ProductStatsProps {
  selectedProduct: Product | undefined;
  productStats: {
    usage: number;
    added: number;
    daysRemaining: number;
    usageRate: number;
    restockDate: Date;
  };
  timeRange: "7days" | "30days" | "90days";
}

const ProductStats = ({ selectedProduct, productStats, timeRange }: ProductStatsProps) => {
  const [usageTimeRange, setUsageTimeRange] = useState<"1month" | "3months" | "6months">("1month");
  const [currentUsageRate, setCurrentUsageRate] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { toast } = useToast();
  
  useEffect(() => {
    const fetchUsageRate = async () => {
      if (!selectedProduct) return;
      
      setIsLoading(true);
      try {
        const days = usageTimeRange === "1month" ? 30 : usageTimeRange === "3months" ? 90 : 180;
        const calculatedRate = await calculateProductUsageRate(selectedProduct.id, days);
        setCurrentUsageRate(calculatedRate);
        console.log(`Fetched usage rate for ${selectedProduct.name}: ${calculatedRate.toFixed(2)} units/day`);
      } catch (error) {
        console.error("Error fetching usage rate:", error);
        toast({
          title: "Error calculating usage rate",
          description: "Could not retrieve the latest usage statistics",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchUsageRate();
  }, [selectedProduct, usageTimeRange, toast]);
  
  if (!selectedProduct) return null;
  
  // Recalculate days remaining based on selected usage rate
  const daysRemaining = currentUsageRate > 0 
    ? Math.floor(selectedProduct.currentStock / currentUsageRate)
    : productStats.daysRemaining;
  
  // Recalculate restock date based on days remaining
  const restockDate = new Date();
  restockDate.setDate(restockDate.getDate() + daysRemaining);

  return (
    <>
      <h2 className="text-xl font-semibold mb-4">
        {selectedProduct.name} Statistics
      </h2>
      
      <div className="mb-4">
        <h3 className="text-sm font-medium mb-2">Average Daily Usage Period:</h3>
        <Tabs 
          value={usageTimeRange} 
          onValueChange={(value: "1month" | "3months" | "6months") => setUsageTimeRange(value)}
          className="w-full sm:w-[300px]"
        >
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="1month">1 Month</TabsTrigger>
            <TabsTrigger value="3months">3 Months</TabsTrigger>
            <TabsTrigger value="6months">6 Months</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <StatsCard
          title="Current Stock"
          value={selectedProduct.currentStock}
          icon={<Package2 className="h-5 w-5 text-blue-500 mr-2" />}
          badgeText="Units"
        />
        
        <StatsCard
          title={`Usage Rate (${usageTimeRange})`}
          value={isLoading ? "..." : currentUsageRate.toFixed(2)}
          icon={<Clock className="h-5 w-5 text-purple-500 mr-2" />}
          badgeText="Units/Day"
          badgeColor="bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-100"
        />
        
        <StatsCard
          title="Days Remaining"
          value={daysRemaining}
          icon={<Calendar className="h-5 w-5 text-orange-500 mr-2" />}
          badgeText={daysRemaining < selectedProduct.minStockAlert ? "Low" : "Days"}
          badgeColor={daysRemaining < selectedProduct.minStockAlert 
            ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100" 
            : "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-100"}
          subtitle={`Restock by: ${format(restockDate, "MMM d, yyyy")}`}
        />
        
        <StatsCard
          title={`${timeRange} Usage`}
          value={productStats.usage}
          icon={<ArrowDownCircle className="h-5 w-5 text-red-500 mr-2" />}
          badgeText="Used"
          badgeColor="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100"
        />
      </div>
    </>
  );
};

export default ProductStats;
